import { defineStore } from "pinia";
import { useLocalStorage } from "@vueuse/core";
import type { RemovableRef } from "@vueuse/core";
import FlrigService, { type FlrigResponse } from "@/services/FlrigService";
import HamlibService from "@/services/HamlibService";
import type { RigControlService, RigControlBackend } from "@/services/RigControlService";
import { rigControlLogger } from "@/utils/loggers";
import { captureException } from "@/utils/sentry";
import { useToastSystem } from "@/composables/useToastSystem";
import type { BandType, CommonModes } from "@/types";

// How long to wait between background reconnect attempts after an established
// connection drops unexpectedly (e.g. the USB cable was pulled).
const RECONNECT_INTERVAL_MS = 5000;

// Give up auto-reconnecting after this long so we don't spin forever when the
// radio isn't coming back. The user can re-test manually any time after.
const RECONNECT_TIMEOUT_MS = 120000; // 2 minutes

// A flapping connection (drop -> auto-reconnect -> drop again) would otherwise
// toast every cycle. After a disconnect toast is shown, further automatic
// disconnect/reconnect toast pairs are suppressed for this long.
const FLAP_TOAST_SUPPRESS_MS = 60000;

// All rig control settings live under a nested pinia/settings/rigControl/*
// namespace. These older flat keys (some shipped with the original Flrig
// feature) are migrated to their new homes once, so upgrading users keep their
// configuration.
const RIG_CONTROL_KEY_MIGRATIONS: ReadonlyArray<readonly [oldKey: string, newKey: string]> = [
  ["pinia/settings/rigControlBackend", "pinia/settings/rigControl/integrationMethod"],
  ["pinia/settings/flrigEnabled", "pinia/settings/rigControl/enabled"],
  ["pinia/settings/flrigHost", "pinia/settings/rigControl/flrig/host"],
  ["pinia/settings/flrigPort", "pinia/settings/rigControl/flrig/port"],
  ["pinia/settings/flrigPowerPollingEnabled", "pinia/settings/rigControl/powerPolling"],
  ["pinia/settings/hamlibModelId", "pinia/settings/rigControl/hamlib/radioId"],
  ["pinia/settings/hamlibModelName", "pinia/settings/rigControl/hamlib/radioName"],
  ["pinia/settings/hamlibSerialPort", "pinia/settings/rigControl/hamlib/serialPort"],
  ["pinia/settings/hamlibBaud", "pinia/settings/rigControl/hamlib/baudRate"],
  ["pinia/settings/hamlibCustomPath", "pinia/settings/rigControl/hamlib/customPath"],
];

/**
 * Parse the user's rigctld port setting into a number for the backend. An empty
 * or out-of-range value means "let rigctld pick a random free port" (undefined).
 * Ports below 1024 are privileged and would fail to bind, so they're rejected.
 */
export function parseRigctldPort(raw: string): number | undefined {
  const trimmed = raw.trim();
  // Decimal digits only - Number() would otherwise accept "1e4", "0x11", etc.
  if (!/^\d+$/.test(trimmed)) return undefined;
  const port = Number(trimmed);
  return port >= 1024 && port <= 65535 ? port : undefined;
}

/**
 * Parse the host:port of an external rigctld. Unlike the internal port, we're
 * only connecting out (not binding), so the full 1-65535 range is valid.
 */
export function parseNetworkPort(raw: string): number | undefined {
  const trimmed = raw.trim();
  // Decimal digits only - Number() would otherwise accept "1e4", "0x11", etc.
  if (!/^\d+$/.test(trimmed)) return undefined;
  const port = Number(trimmed);
  return port >= 1 && port <= 65535 ? port : undefined;
}

/** Copy any legacy flat keys into the nested namespace, then clear the old keys. */
export function migrateRigControlSettings() {
  try {
    for (const [oldKey, newKey] of RIG_CONTROL_KEY_MIGRATIONS) {
      const legacy = window.localStorage.getItem(oldKey);
      if (legacy !== null && window.localStorage.getItem(newKey) === null) {
        window.localStorage.setItem(newKey, legacy);
      }
      if (legacy !== null) {
        window.localStorage.removeItem(oldKey);
      }
    }
  } catch {
    // localStorage unavailable - non-fatal
  }
}

interface RigControlState {
  enabled: RemovableRef<boolean>;
  backend: RemovableRef<RigControlBackend>;
  connectionTested: boolean;
  connectionSuccess: boolean;
  connectionError: string;
  lastErrorCode: string;
  flrigHost: RemovableRef<string>;
  flrigPort: RemovableRef<string>;
  hamlibModelId: RemovableRef<number>;
  hamlibModelName: RemovableRef<string>;
  hamlibSerialPort: RemovableRef<string>;
  hamlibBaud: RemovableRef<number>;
  hamlibCustomPath: RemovableRef<string>;
  hamlibRigctldPort: RemovableRef<string>;
  hamlibConnectionMode: RemovableRef<'managed' | 'external'>;
  hamlibNetworkHost: RemovableRef<string>;
  hamlibNetworkPort: RemovableRef<string>;
  powerPollingEnabled: RemovableRef<boolean>;
  service: RigControlService | null;
  availableModes: string[];
  power: number;
  mode: string;
  frequency: number;
  heartbeat: ReturnType<typeof setInterval> | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  connecting: boolean;
  reconnecting: boolean;
  reconnectUntil: number;
  detecting: boolean;
  detectProgress: string;
  lastDisconnectToastAt: number;
  disconnectToastShown: boolean;
}

export const useRigControlStore = defineStore("rigControl", {
  state: (): RigControlState => {
    // Move any legacy flat keys into the nested namespace before the
    // useLocalStorage refs below read them.
    migrateRigControlSettings();
    return {
    enabled: useLocalStorage("pinia/settings/rigControl/enabled", false),
    backend: useLocalStorage<RigControlBackend>("pinia/settings/rigControl/integrationMethod", "flrig"),
    connectionTested: false,
    connectionSuccess: false,
    connectionError: "",
    lastErrorCode: "",
    flrigHost: useLocalStorage("pinia/settings/rigControl/flrig/host", "localhost"),
    flrigPort: useLocalStorage("pinia/settings/rigControl/flrig/port", "12345"),
    hamlibModelId: useLocalStorage("pinia/settings/rigControl/hamlib/radioId", 0),
    hamlibModelName: useLocalStorage("pinia/settings/rigControl/hamlib/radioName", ""),
    hamlibSerialPort: useLocalStorage("pinia/settings/rigControl/hamlib/serialPort", ""),
    hamlibBaud: useLocalStorage("pinia/settings/rigControl/hamlib/baudRate", 115200),
    hamlibCustomPath: useLocalStorage("pinia/settings/rigControl/hamlib/customPath", ""),
    // Empty = random free port (current behavior); a fixed port lets external
    // tools connect to HAMRS's internal rigctld.
    hamlibRigctldPort: useLocalStorage("pinia/settings/rigControl/hamlib/rigctldPort", ""),
    // 'managed' spawns our own rigctld; 'external' connects to one the operator
    // is already running so the radio can be shared with fldigi/WSJT-X/etc.
    hamlibConnectionMode: useLocalStorage<'managed' | 'external'>("pinia/settings/rigControl/hamlib/connectionMode", "managed"),
    hamlibNetworkHost: useLocalStorage("pinia/settings/rigControl/hamlib/networkHost", "127.0.0.1"),
    hamlibNetworkPort: useLocalStorage("pinia/settings/rigControl/hamlib/networkPort", "4532"),
    powerPollingEnabled: useLocalStorage("pinia/settings/rigControl/powerPolling", true),
    service: null,
    availableModes: [],
    power: 0,
    mode: "",
    frequency: 0,
    heartbeat: null,
    reconnectTimer: null,
    connecting: false,
    reconnecting: false,
    reconnectUntil: 0,
    detecting: false,
    detectProgress: "",
    lastDisconnectToastAt: 0,
    disconnectToastShown: false,
    };
  },

  getters: {
    rigControlAvailable: (state): boolean => {
      return state.connectionTested &&
        state.connectionSuccess &&
        state.service !== null;
    },
    rigControlEnabled: (state): boolean => {
      return state.enabled && state.connectionSuccess;
    },
    hamlibConfigured: (state): boolean => {
      // External mode only needs a reachable host:port - no local model/serial.
      if (state.hamlibConnectionMode === 'external') {
        return Boolean(state.hamlibNetworkHost.trim() && parseNetworkPort(state.hamlibNetworkPort));
      }
      return Boolean(state.hamlibModelId && state.hamlibSerialPort && state.hamlibBaud);
    },
    isReconnecting: (state): boolean => {
      return state.reconnecting;
    },
  },

  actions: {
    /**
     * Handles a response from the active rig control service and updates store state
     */
    handleRigResponse<T>(
      response: FlrigResponse<T>,
      successCallback?: (value: T) => void,
      errorMessage: string = "Operation failed"
    ) {
      // A successful response counts even when it carries no value: set
      // operations (setFrequency/setMode) resolve to { success: true } with no
      // payload, and gating on `value !== undefined` used to misfile those as
      // failures - skipping the optimistic state update and stamping a spurious
      // connectionError on every successful QSY. Reads only ever carry a value
      // on success, so passing it straight through stays correct for them too.
      if (response.success) {
        this.connectionError = "";
        this.lastErrorCode = "";
        successCallback?.(response.value as T);
        return true;
      } else {
        // Record the reason *before* stopping the heartbeat, since stopHeartbeat
        // reads connectionError/lastErrorCode to build the disconnect toast.
        this.connectionError = response.error || errorMessage;
        this.lastErrorCode = response.errorCode || "";
        if (response.errorCode === 'NO_RADIO') {
          this.stopHeartbeat();
        }
        return false;
      }
    },

    startHeartbeat() {
      rigControlLogger("💚 Starting Heartbeat...");
      if (this.heartbeat) return;
      this.pollRadio();
      this.heartbeat = setInterval(() => this.pollRadio(), 5000);
    },

    /** Brief success toast confirming a connection (names the radio for hamlib). */
    notifyConnected() {
      // A deliberate (re)connect starts a fresh notification cycle: if this
      // connection drops, the user should hear about it right away.
      this.lastDisconnectToastAt = 0;
      this.disconnectToastShown = false;
      const radio = this.backend === 'hamlib' ? this.hamlibModelName : '';
      // Toast must never throw (plugin not ready, etc.) - it's only a courtesy
      try {
        useToastSystem().rigControlConnectedToast(radio || undefined);
      } catch (error) {
        rigControlLogger("Failed to show connected toast:", error);
      }
    },

    /**
     * Stops polling and tears down the active backend. Pass reason 'user' for
     * an intentional toggle-off (silent); the default 'error' surfaces a toast
     * and a Sentry breadcrumb, but only when an established connection actually
     * drops (i.e. the heartbeat was running) - not on a failed initial test,
     * which the settings UI already reports.
     */
    stopHeartbeat(reason: 'user' | 'error' = 'error') {
      // An explicit user stop also cancels any in-flight auto-reconnect.
      if (reason === 'user') {
        this.stopReconnect();
      }
      if (this.heartbeat) {
        rigControlLogger("❤️ Stopping Heartbeat...");
        clearInterval(this.heartbeat);
        this.heartbeat = null;
        this.enabled = false;
        this.connectionSuccess = false;
        this.connectionTested = false;
        this.releaseService();

        if (reason === 'error') {
          // A live connection dropped: tell the user, then keep trying in the
          // background so plugging the radio back in restores rig control on
          // its own.
          this.notifyConnectionLost(this.connectionError);
          this.startReconnect();
        }
      }
    },

    /**
     * Begin polling for the radio to come back after an unexpected drop. Uses a
     * self-scheduling timeout (not setInterval) so attempts never overlap - each
     * reconnect attempt can itself take a few seconds.
     */
    startReconnect() {
      if (this.reconnecting || this.connectionSuccess) return;
      rigControlLogger("🔄 Scheduling rig control reconnect...");
      this.reconnecting = true;
      this.reconnectUntil = Date.now() + RECONNECT_TIMEOUT_MS;
      this.reconnectTimer = setTimeout(() => this.attemptReconnect(), RECONNECT_INTERVAL_MS);
    },

    async attemptReconnect() {
      this.reconnectTimer = null;
      if (!this.reconnecting) return;
      rigControlLogger("🔄 Attempting to reconnect to the radio...");
      // testConnection re-creates the service and (for hamlib) re-spawns
      // rigctld; it fails fast when the serial device is still gone. On success
      // it starts the heartbeat and calls stopReconnect (clearing `reconnecting`).
      await this.testConnection();
      if (this.connectionSuccess) {
        rigControlLogger("✅ Rig control reconnected");
        // Only pair a "reconnected" toast with a disconnect toast the user
        // actually saw - a flap-suppressed drop recovers silently too.
        if (this.disconnectToastShown) {
          this.disconnectToastShown = false;
          try {
            useToastSystem().rigControlReconnectedToast();
          } catch (error) {
            rigControlLogger("Failed to show reconnect toast:", error);
          }
        }
        return;
      }
      if (!this.reconnecting) return;
      // Stop after the timeout so we don't retry forever.
      if (Date.now() >= this.reconnectUntil) {
        rigControlLogger("🛑 Reconnect timed out; giving up");
        this.stopReconnect();
        try {
          useToastSystem().rigControlReconnectFailedToast();
        } catch (error) {
          rigControlLogger("Failed to show reconnect-failed toast:", error);
        }
        return;
      }
      if (!this.reconnectTimer) {
        // Still down (and not cancelled mid-attempt) - schedule the next try.
        this.reconnectTimer = setTimeout(() => this.attemptReconnect(), RECONNECT_INTERVAL_MS);
      }
    },

    stopReconnect() {
      if (!this.reconnecting && !this.reconnectTimer) return;
      rigControlLogger("🛑 Stopping rig control reconnect attempts");
      this.reconnecting = false;
      this.reconnectUntil = 0;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    },

    /**
     * Non-fatal: tell the user a live rig connection dropped, and log it. Leads
     * with a friendly, actionable headline (the dominant cause is the radio
     * being powered off or unplugged) and keeps the backend's raw reason - e.g.
     * hamlib's "IO error..." - as a muted detail line.
     */
    notifyConnectionLost(rawReason: string) {
      // A dropped connection is normal operation (radio unplugged or powered
      // off), not a code error - log it locally, but don't raise a Sentry issue.
      rigControlLogger("Rig control connection lost:", rawReason);
      // Flap guard: while the connection is bouncing, only the first drop in
      // the window toasts. disconnectToastShown also gates the matching
      // "reconnected" toast, so a suppressed cycle is fully silent.
      if (Date.now() - this.lastDisconnectToastAt < FLAP_TOAST_SUPPRESS_MS) {
        this.disconnectToastShown = false;
        return;
      }
      this.lastDisconnectToastAt = Date.now();
      this.disconnectToastShown = true;
      // Only show the raw reason when it's a genuine radio diagnostic from
      // hamlib (NO_RADIO covers timeout / IO error / wrong baud / powered-off).
      // Internal states like "Rig control is not running" (CONNECTION_ERROR)
      // only confuse, so the actionable headline stands alone for those.
      const detail = this.lastErrorCode === 'NO_RADIO' ? (rawReason || undefined) : undefined;
      // Toast must never throw (plugin not ready, etc.) - it's only a courtesy
      try {
        useToastSystem().rigControlDisconnectedToast({
          message: "Check that your radio is powered on and the USB cable is connected.",
          detail,
        });
      } catch (error) {
        rigControlLogger("Failed to show disconnect toast:", error);
      }
    },

    /** Disconnects the active backend (e.g. stops the rigctld daemon) and clears it */
    releaseService() {
      const service = this.service;
      this.service = null;
      if (service) {
        Promise.resolve(service.disconnect()).catch((error) =>
          rigControlLogger("Error disconnecting rig control service:", error)
        );
      }
    },

    async pollRadio() {
      this.initializeService();
      if (!this.rigControlEnabled || !this.service) {
        this.stopHeartbeat();
        return;
      }

      try {
        const frequencyResponse = await this.getFrequency();
        const modeResponse = await this.getMode();
        // When power polling is disabled (e.g. a QRP rig feeding an external
        // amp), skip reading power so the logged value isn't overwritten.
        const powerResponse = this.powerPollingEnabled ? await this.getPower() : true;

        if (!frequencyResponse || !modeResponse || !powerResponse) {
          this.stopHeartbeat();
        }
      } catch (error) {
        rigControlLogger("Poll Error:", error);
        this.connectionError = error instanceof Error ? error.message : "Polling failed unexpectedly.";
        captureException(error, { feature: 'rig-control', backend: this.backend, phase: 'poll' });
        this.stopHeartbeat();
      }
    },

    initializeService() {
      if (!this.service) {
        if (this.backend === 'hamlib') {
          this.service = new HamlibService({
            modelId: this.hamlibModelId,
            modelName: this.hamlibModelName,
            port: this.hamlibSerialPort,
            baud: this.hamlibBaud,
            customPath: this.hamlibCustomPath || undefined,
            rigctldPort: parseRigctldPort(this.hamlibRigctldPort),
            connectionMode: this.hamlibConnectionMode,
            networkHost: this.hamlibNetworkHost.trim(),
            networkPort: parseNetworkPort(this.hamlibNetworkPort),
          });
        } else {
          this.service = new FlrigService(this.flrigHost, this.flrigPort);
        }
      }
    },

    /**
     * Switches between rig control backends. Only one backend is ever active:
     * switching tears down the current one before the next is initialized.
     */
    setBackend(backend: RigControlBackend) {
      if (this.backend === backend) return;
      rigControlLogger(`Switching rig control backend to ${backend}`);
      this.stopReconnect();
      if (this.heartbeat) {
        clearInterval(this.heartbeat);
        this.heartbeat = null;
      }
      this.enabled = false;
      this.connectionTested = false;
      this.connectionSuccess = false;
      this.connectionError = "";
      this.releaseService();
      this.backend = backend;
    },

    async setFrequency(freq: number) {
      this.initializeService();
      if (!this.service) return;

      const response = await this.service.setFrequency(freq);
      return this.handleRigResponse(
        response,
        () => this.frequency = freq,
        "Failed to set frequency"
      );
    },

    async getFrequency() {
      if (!this.service) return false;

      const response = await this.service.getFrequency();
      return this.handleRigResponse(
        response,
        (value) => this.frequency = value,
        "Failed to get frequency"
      );
    },

    async getMode() {
      const service = this.service;
      if (!service) return false;

      const response = await service.getMode();
      return this.handleRigResponse(
        response,
        (value) => this.mode = service.mapToCommonMode(value),
        "Failed to get mode"
      );
    },

    async getPower() {
      if (!this.service) return false;

      const response = await this.service.getPower();
      return this.handleRigResponse(
        response,
        (value) => this.power = value,
        "Failed to get power"
      );
    },

    async setMode(mode: CommonModes, band?: BandType) {
      this.initializeService();
      const service = this.service;
      if (!service) return;

      const response = await service.setMode(mode, band);
      return this.handleRigResponse(
        response,
        () => this.mode = service.mapToCommonMode(mode),  // Use the input mode directly
        "Failed to set mode"
      );
    },

    /**
     * Tune the rig to a spot in one shot: set the mode (sideband resolved from
     * the band), set the frequency, then immediately read the rig back so the
     * spot/rig status display reflects the change right away instead of waiting
     * up to a full heartbeat interval (~5s) for the next scheduled poll. The set
     * calls update store state optimistically; the follow-up poll replaces those
     * with the rig's actual settings (it may snap the frequency, and power is
     * never optimistic). The heartbeat keeps its own timer, so this extra read
     * doesn't change the periodic polling cadence. Backs the spot Tune/Copy
     * buttons and the spot scanner.
     */
    async tuneToSpot(frequencyHz: number, mode: CommonModes, band: BandType) {
      if (await this.setMode(mode, band) === false) return;
      if (await this.setFrequency(frequencyHz) === false) return;
      await this.pollRadio();
    },

    async testConnection(options?: { notify?: boolean }) {
      rigControlLogger(`Testing ${this.backend} connection`);
      this.initializeService();
      // Capture the service locally: a config change can release this.service
      // while the test is awaiting
      const service = this.service!;

      this.connecting = true;
      try {
        const response = await service.testConnection();

        this.connectionTested = true;
        this.connectionSuccess = response.success;
        this.enabled = response.success;

        if (response.success) {
          this.availableModes = service.availableModes;
          this.connectionError = "";
          this.lastErrorCode = "";
          this.stopReconnect();
          this.startHeartbeat();
          // Only a user-initiated connect from the config panel asks to be
          // notified; bootstrap and the reconnect loop stay quiet.
          if (options?.notify) {
            this.notifyConnected();
          }
        } else {
          this.connectionError = response.error || (this.backend === 'hamlib'
            ? "Unable to connect to the radio. Check the model, port, and baud rate."
            : "Unable to connect to Flrig. Check host/port.");
          this.stopHeartbeat();
          // Release the serial port so a failed config doesn't hold it open
          service.disconnect();
        }

        return response;
      } catch (error) {
        this.enabled = false;
        this.connectionTested = true;
        this.connectionSuccess = false;
        this.connectionError = "An unexpected error occurred while testing the connection.";
        rigControlLogger("testConnection Error:", error);
        captureException(error, { feature: 'rig-control', backend: this.backend, phase: 'testConnection' });
        this.stopHeartbeat();
        return {
          success: false,
          error: "An unexpected error occurred",
          errorCode: 'CONNECTION_ERROR'
        };
      } finally {
        this.connecting = false;
      }
    },

    /**
     * One-click hamlib setup: scans serial ports and probes popular radios
     * until one answers, then saves the working configuration and connects.
     */
    async detectRadio() {
      const bridge = (window as any).api?.hamlib;
      if (!bridge) {
        this.connectionError = "Built-in rig control is only available in the HAMRS desktop app.";
        return false;
      }
      if (this.detecting) return false;

      this.detecting = true;
      this.detectProgress = "Scanning serial ports...";
      this.connectionError = "";

      // Free the serial port before probing
      if (this.heartbeat) {
        clearInterval(this.heartbeat);
        this.heartbeat = null;
      }
      this.enabled = false;
      this.connectionTested = false;
      this.connectionSuccess = false;
      this.releaseService();

      try {
        bridge.onDetectProgress((progress: { model: string; baud: number; portLabel: string; attempt: number; totalAttempts: number }) => {
          this.detectProgress = `Trying ${progress.model} @ ${progress.baud} baud on ${progress.portLabel} (${progress.attempt}/${progress.totalAttempts})...`;
        });

        // With a model already chosen, detection only hunts for its port/baud
        const response = await bridge.detect({
          customPath: this.hamlibCustomPath || undefined,
          modelId: this.hamlibModelId || undefined,
        });

        if (response.success && response.result) {
          rigControlLogger("🪄 Radio detected:", response.result);
          this.hamlibModelId = response.result.modelId;
          this.hamlibModelName = `${response.result.manufacturer} ${response.result.modelName}`;
          this.hamlibSerialPort = response.result.port;
          this.hamlibBaud = response.result.baud;
          // Detection always targets a local USB radio, so force the managed
          // hamlib path before testing - otherwise a prior Flrig/external-rigctld
          // selection would make testConnection ignore the detected radio.
          this.backend = 'hamlib';
          this.hamlibConnectionMode = 'managed';
          this.service = null;
          await this.testConnection({ notify: true });
          return true;
        }

        if (!response.cancelled) {
          this.connectionError = response.error || "No radio detected.";
        }
        return false;
      } catch (error) {
        rigControlLogger("detectRadio Error:", error);
        captureException(error, { feature: 'rig-control', backend: 'hamlib', phase: 'detect' });
        this.connectionError = "An unexpected error occurred while detecting the radio.";
        return false;
      } finally {
        this.detecting = false;
        this.detectProgress = "";
      }
    },

    async cancelDetect() {
      try {
        const bridge = (window as any).api?.hamlib;
        await bridge?.cancelDetect();
      } catch (error) {
        rigControlLogger("cancelDetect Error:", error);
      }
    },

    async toggleRigControl() {
      // While auto-reconnecting (disconnected, but retrying), a click cancels
      // the retry loop rather than kicking off a redundant connect.
      if (this.reconnecting) {
        this.stopReconnect();
        return;
      }
      if (this.enabled) {
        this.enabled = false;
        this.stopHeartbeat('user');
        return;
      }
      // Ignore a click while a manual connect is already in flight so rapid
      // clicks can't start overlapping testConnection() calls (double rigctld
      // starts, double toasts). Placed after the reconnect/enabled checks so a
      // click can still cancel an in-progress reconnect attempt.
      if (this.connecting) return;
      // Disconnected: try to (re)connect straight from the nav indicator, so the
      // user doesn't have to open Settings. The indicator has no inline error,
      // so surface the outcome as a toast (success names the radio; failure
      // shows why) - otherwise a failed click looks like it did nothing.
      const response = await this.testConnection({ notify: true });
      if (!response?.success) {
        this.notifyConnectFailed();
      }
    },

    /** Toast for a failed manual connect attempt (e.g. from the nav indicator). */
    notifyConnectFailed() {
      try {
        useToastSystem().rigControlDisconnectedToast({
          message: this.connectionError || "Couldn't connect to the radio. Check it's powered on and connected.",
        });
      } catch (error) {
        rigControlLogger("Failed to show connect-failed toast:", error);
      }
    },

    togglePowerPolling() {
      this.powerPollingEnabled = !this.powerPollingEnabled;
    },

    updateHostAndPort(host: string, port: string) {
      this.flrigHost = host;
      this.flrigPort = port;
      this.releaseService();
    },

    updateHamlibConfig(config: { modelId?: number; modelName?: string; port?: string; baud?: number; customPath?: string; rigctldPort?: string; connectionMode?: 'managed' | 'external'; networkHost?: string; networkPort?: string }) {
      // No-op when nothing changed: FormKit echoes v-model values back through
      // its computed setters, and tearing down the service on an echo breaks
      // an in-flight connection test.
      let changed = false;
      if (config.modelId !== undefined && config.modelId !== this.hamlibModelId) {
        this.hamlibModelId = config.modelId;
        changed = true;
      }
      if (config.modelName !== undefined && config.modelName !== this.hamlibModelName) {
        this.hamlibModelName = config.modelName;
        changed = true;
      }
      if (config.port !== undefined && config.port !== this.hamlibSerialPort) {
        this.hamlibSerialPort = config.port;
        changed = true;
      }
      if (config.baud !== undefined && config.baud !== this.hamlibBaud) {
        this.hamlibBaud = config.baud;
        changed = true;
      }
      if (config.customPath !== undefined && config.customPath !== this.hamlibCustomPath) {
        this.hamlibCustomPath = config.customPath;
        changed = true;
      }
      if (config.rigctldPort !== undefined && config.rigctldPort !== this.hamlibRigctldPort) {
        this.hamlibRigctldPort = config.rigctldPort;
        changed = true;
      }
      if (config.connectionMode !== undefined && config.connectionMode !== this.hamlibConnectionMode) {
        this.hamlibConnectionMode = config.connectionMode;
        changed = true;
      }
      if (config.networkHost !== undefined && config.networkHost !== this.hamlibNetworkHost) {
        this.hamlibNetworkHost = config.networkHost;
        changed = true;
      }
      if (config.networkPort !== undefined && config.networkPort !== this.hamlibNetworkPort) {
        this.hamlibNetworkPort = config.networkPort;
        changed = true;
      }
      if (changed) this.releaseService();
    },

    /**
     * Reset all saved rig control settings back to defaults. Assigning the
     * useLocalStorage-backed state persists the defaults automatically. Backs
     * the "Reset" button in config.
     */
    resetRigControlSettings() {
      this.stopReconnect();
      this.stopHeartbeat('user');
      // stopHeartbeat only releases the service when a heartbeat was running, so
      // drop any cached service explicitly - otherwise a stale instance (wrong
      // backend/config) could be reused on the next connect.
      this.releaseService();

      this.backend = 'flrig';
      this.flrigHost = 'localhost';
      this.flrigPort = '12345';
      this.hamlibModelId = 0;
      this.hamlibModelName = '';
      this.hamlibSerialPort = '';
      this.hamlibBaud = 115200;
      this.hamlibCustomPath = '';
      this.hamlibRigctldPort = '';
      this.hamlibConnectionMode = 'managed';
      this.hamlibNetworkHost = '127.0.0.1';
      this.hamlibNetworkPort = '4532';
      this.powerPollingEnabled = true;
      this.enabled = false;

      this.connectionTested = false;
      this.connectionSuccess = false;
      this.connectionError = '';
      this.lastErrorCode = '';
      this.availableModes = [];
    },
  },
});
